import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { isEntitled, type OrgBillingDetails } from '@charitystack/entitlements';
import { checkPermission } from '../utils/auth';
import { MARKETING_VIEW_PERMISSIONS } from '../utils/permissions';

// The `pocketflows_marketing_tab` rollout flag was removed after the
// PocketFlows Marketing launch reached 100% rollout (2026-08). The marketing
// view permissions below are the remaining, permanent gate.

// The SMS master flag. Kept as a global kill switch on top of the plan gate
// below (SMS shows only when the flag is ON *and* the org's plan includes SMS).
// Flipping it OFF hides every SMS surface instantly regardless of plan; it
// survived the marketing-tab rollout-flag removal because it is a kill switch,
// not a rollout flag.
const SMS_FLAG_KEY = 'pocketflows_sms';

// PE-1766 follow-up — gates the "Sending number" card on the SMS settings tab
// (the dedicated-number REQUEST surface). Separate from `pocketflows_sms` so
// the request flow can be rolled out/killed without touching the rest of the
// SMS surfaces (credits, opt-outs, embed step). Default OFF (missing /
// unresolved / ad-blocked flag hides the card).
const SMS_NUMBER_REQUESTS_FLAG_KEY = 'pocketflows_sms_number_requests';

// Whether an org's PLAN can request its own DEDICATED sending number — a Pro
// feature (Plus gets SMS via the CharityStack shared number; a dedicated
// number is the Pro differentiator). Slug check, fail-closed: unknown/absent
// plans can't request. Mirrors the marketing backend's
// `planAllowsDedicatedSmsNumber` — keep the two lists in sync.
export function planIncludesDedicatedSmsNumber(
  plan: string | null | undefined,
): boolean {
  const upper = (plan ?? '').toUpperCase();
  return upper === 'PRO' || upper === 'ENTERPRISE';
}

// Whether an org's PLAN includes SMS — the second half of the SMS gate. SMS is
// the `broadcasts_sms` FEATURE boolean (ON at Base+), DECOUPLED from the
// `sms_included_per_month` credit allotment as of the SMS split: a plan can be
// "on SMS" with 0 covered credits. This mirrors the marketing backend's
// `sms-availability.ts planIncludesSms` (PE-1679) — the same signal that gates
// the SMS step in the PocketFlows embed — so the dashboard UI and the embed agree
// on who can use SMS. (Do NOT reintroduce a `sms_included_per_month > 0` gate here;
// that limit is covered-credits only now.)
//
// FAIL CLOSED: `billingDetails.plan` is NOT guaranteed canonical (it can hold a
// legacy Clerk subscription slug — see accounts-backend get_merchant). isEntitled
// on an absent plan resolves to STARTER (no broadcasts_sms) and on a present-but-
// unknown slug throws; either way we treat it as "no SMS" — the safe posture.
export function planIncludesSms(plan: string | null | undefined): boolean {
  try {
    return isEntitled(
      { plan: (plan ?? null) as OrgBillingDetails['plan'] },
      'broadcasts_sms',
    );
  } catch {
    return false;
  }
}

export type MarketingTierState = 'loading' | 'enabled' | 'disabled';

// "Can this user see ANY Marketing surface" — true if they hold at least
// one of the per-entity view permissions (broadcasts / segments / email
// templates). Each tab/page additionally gates on its own view permission
// (e.g. checkPermission(P.VIEW_SEGMENTS) on SegmentsPage); this is the
// section-level check only.
function hasAnyMarketingViewPermission(): boolean {
  return MARKETING_VIEW_PERMISSIONS.some((perm) => checkPermission(perm));
}

/**
 * The single gate consumed by every PocketFlows Marketing surface.
 *
 * Since the `pocketflows_marketing_tab` rollout flag was retired at full
 * rollout, this is a permission check only: at least one marketing view
 * permission (P.VIEW_BROADCASTS / P.VIEW_SEGMENTS — revocable per role).
 *
 * Used at three gate points (see initiatives/pocketflows/frontend-plan.md §5.5):
 *   - Sidebar entry (Sidebar.js — the section; each tab also checks its own view permission)
 *   - Route registration (App.js — pages return null when not 'enabled')
 *   - Settings tab filter (SettingsContent.js ALL_TABS array)
 *
 * 'loading' is kept in MarketingTierState for existing callers that branch on
 * it, but is never returned anymore — permission resolution is synchronous.
 */
export function useMarketingTierEnabled(): MarketingTierState {
  return hasAnyMarketingViewPermission() ? 'enabled' : 'disabled';
}

/**
 * Convenience boolean for the common case where loading should be treated as
 * disabled. Equivalent to `useMarketingTierEnabled() === 'enabled'`.
 */
export function useMarketingTierGate(): boolean {
  return useMarketingTierEnabled() === 'enabled';
}

/**
 * Non-reactive snapshot of the same gate, for class components and table
 * column render functions where hooks can't be used. Same permission-only
 * check as useMarketingTierEnabled.
 */
export function isMarketingTierEnabledNow(): boolean {
  return hasAnyMarketingViewPermission();
}

/**
 * The single gate every SMS surface calls. Reactive — re-renders when PostHog
 * resolves flags. Returns `true` ONLY when BOTH hold:
 *   1. the `pocketflows_sms` flag is explicitly on (the global kill switch), and
 *   2. the org's `plan` includes SMS (Base+ — see `planIncludesSms`).
 *
 * `undefined` (flag unresolved / PostHog blocked) and `false` both map to `false`;
 * an unknown/absent plan also maps to `false`. **Default-off is intentional and
 * load-bearing**: an ad-blocked or unresolved flag must never *reveal* SMS.
 *
 * `plan` is the raw `billingDetails.plan` slug the caller already holds (from the
 * merchant prop or `useEntitlements().plan`). Passed in rather than read from a
 * context so the gate works on pages without an EntitlementsProvider mounted
 * (e.g. Settings → Marketing).
 */
export function useSmsSurfacesEnabled(plan: string | null | undefined): boolean {
  const [flagOn, setFlagOn] = useState<boolean>(
    () => posthog.isFeatureEnabled(SMS_FLAG_KEY) === true,
  );

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setFlagOn(posthog.isFeatureEnabled(SMS_FLAG_KEY) === true);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return flagOn && planIncludesSms(plan);
}

/**
 * Non-reactive snapshot of useSmsSurfacesEnabled, for class components and
 * table cell render functions where hooks can't be used (e.g. the per-contact
 * SMS opt-out toggle). Same flag-AND-plan gate, same default-off posture.
 */
export function isSmsSurfacesEnabledNow(plan: string | null | undefined): boolean {
  return posthog.isFeatureEnabled(SMS_FLAG_KEY) === true && planIncludesSms(plan);
}

/**
 * Flag-only SMS gate for ADVERTISING / upsell copy — the "Plus includes SMS"
 * messaging on the billing plan cards, the "Scaling past Plus?" strip, and the
 * onboarding subscribe context panel. These surfaces exist to SELL the upgrade,
 * so they must show to STARTER/BASE orgs (the ones being upsold) — they are
 * gated on the `pocketflows_sms` master flag ONLY, deliberately NOT on the
 * viewer's current plan. Same reactive + default-off posture as
 * `useSmsSurfacesEnabled`. Do NOT use this for a surface that reveals a
 * functional SMS capability — use `useSmsSurfacesEnabled(plan)` for those.
 */
export function useSmsFlagEnabled(): boolean {
  const [flagOn, setFlagOn] = useState<boolean>(
    () => posthog.isFeatureEnabled(SMS_FLAG_KEY) === true,
  );

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setFlagOn(posthog.isFeatureEnabled(SMS_FLAG_KEY) === true);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return flagOn;
}

/**
 * Gate for the "Sending number" card on Settings → Marketing → SMS (PE-1766).
 * Flag-only — visibility. The PLAN split (Pro can request; Plus sees the card
 * with a Pro upsell) is layered on top via `planIncludesDedicatedSmsNumber`,
 * and the backend independently enforces the plan on requestSmsNumber (the
 * FE gate is courtesy). Same reactive + default-off posture as the other
 * PostHog gates in this file.
 */
export function useSmsNumberRequestsFlagEnabled(): boolean {
  const [flagOn, setFlagOn] = useState<boolean>(
    () => posthog.isFeatureEnabled(SMS_NUMBER_REQUESTS_FLAG_KEY) === true,
  );

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      setFlagOn(posthog.isFeatureEnabled(SMS_NUMBER_REQUESTS_FLAG_KEY) === true);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  return flagOn;
}
